import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, Play, Volume2, GraduationCap, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_VALUE = "__default__";

function VoicePicker({ testid, value, defaultId, speakers, onChange, onPreview, previewing }) {
  const female = speakers.filter((s) => s.gender === "female");
  const male = speakers.filter((s) => s.gender === "male");
  const selected = value || DEFAULT_VALUE;
  const effective = value || defaultId;
  return (
    <div className="flex items-center gap-2">
      <Select value={selected} onValueChange={(v) => onChange(v === DEFAULT_VALUE ? "" : v)}>
        <SelectTrigger className="rounded-xl w-full max-w-[220px]" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={DEFAULT_VALUE}>Default{defaultId ? ` (${defaultId})` : ""}</SelectItem>
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-wider text-pink-500">Female</SelectLabel>
            {female.map((s) => (
              <SelectItem key={s.id} value={s.id} className="capitalize">{s.id}</SelectItem>
            ))}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-wider text-sky-500">Male</SelectLabel>
            {male.map((s) => (
              <SelectItem key={s.id} value={s.id} className="capitalize">{s.id}</SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <Button
        type="button" size="icon" variant="outline"
        className="rounded-full shrink-0 h-9 w-9"
        title={`Preview "${effective}"`}
        onClick={() => onPreview(effective)}
        disabled={previewing}
        data-testid={`${testid}-preview`}
      >
        {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export default function DevAdminTutorVoices() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [speakers, setSpeakers] = useState([]);
  const [subjects, setSubjects] = useState([]);           // [{name, voice, default}]
  const [tutor, setTutor] = useState({});                  // {subject: voice}
  const [guidance, setGuidance] = useState("");
  const [guidanceDefault, setGuidanceDefault] = useState("");
  const [previewing, setPreviewing] = useState("");        // key currently previewing
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/devadmin/tts-voices");
      setSpeakers(data.speakers || []);
      setSubjects(data.subjects || []);
      setGuidance(data.guidance_voice || "");
      setGuidanceDefault(data.guidance_default || "");
      const t = {};
      (data.subjects || []).forEach((s) => { if (s.voice) t[s.name] = s.voice; });
      setTutor(t);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load voices");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const preview = async (key, speaker) => {
    if (!speaker) return;
    setPreviewing(key);
    try {
      const { data } = await api.get(`/devadmin/tts-voices/preview`, {
        params: { speaker, lang: "english" }, responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Preview failed");
    } finally { setPreviewing(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/devadmin/tts-voices", {
        tutor_voices: tutor, guidance_voice: guidance || null,
      });
      toast.success("Voices saved — applied instantly");
      setTutor(data.tutor_voices || {});
      setGuidance(data.guidance_voice || "");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="devadmin-tutor-voices">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
          Choose the spoken voice for the <b>AI Tutor</b> (per subject) and the <b>Teacher Guidance</b> mentor.
          Use <Play className="inline h-3 w-3" /> to hear a voice. Saved changes apply instantly — no redeploy.
        </p>
        <Button onClick={save} disabled={saving} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="tts-voices-save">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save voices
        </Button>
      </div>

      {/* Teacher Guidance */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
          <GraduationCap className="h-4 w-4 text-indigo-500" /> Teacher Guidance mentor
        </div>
        <VoicePicker
          testid="tts-guidance-voice"
          value={guidance} defaultId={guidanceDefault} speakers={speakers}
          onChange={setGuidance}
          onPreview={(spk) => preview("guidance", spk)}
          previewing={previewing === "guidance"}
        />
      </div>

      {/* Tutor voices per subject */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
          <Sparkles className="h-4 w-4 text-amber-500" /> AI Tutor — voice per subject
        </div>
        {subjects.length === 0 ? (
          <p className="text-sm text-slate-400">No tuition subjects found yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {subjects.map((s) => (
              <div key={s.name} className="flex items-center justify-between gap-3 py-1 border-b border-slate-100 dark:border-slate-700/60">
                <div className="flex items-center gap-2 min-w-0">
                  <Volume2 className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate" title={s.name}>{s.name}</span>
                </div>
                <VoicePicker
                  testid={`tts-subject-voice-${s.name}`}
                  value={tutor[s.name] || ""} defaultId={s.default} speakers={speakers}
                  onChange={(v) => setTutor((p) => { const n = { ...p }; if (v) n[s.name] = v; else delete n[s.name]; return n; })}
                  onPreview={(spk) => preview(`subj:${s.name}`, spk)}
                  previewing={previewing === `subj:${s.name}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
